import { routes } from './routes-constants'
import { DEFAULT_TIMEZONE } from '~/utils/constants'

const pendingRequests = new Map()
const getRequestKey = config => `${config.method}:${config.url}`
let hideDefaultToast = false

function accessObject(value) {
  const keys = value.split('.')
  let current = routes
  for (const key of keys) {
    if (current[key]) {
      current = current[key]
    } else {
      return undefined
    }
  }
  return current
}

export default function (context) {
  // converting local time to UTC time
  const utcTime = date => {
    if (date) {
      return context.$dayjs.tz(date, DEFAULT_TIMEZONE).utc().format('YYYY-MM-DD HH:mm:ss')
    }
    return context.$dayjs().tz(DEFAULT_TIMEZONE).utc().format('YYYY-MM-DD HH:mm:ss')
  }
  context.$axios.onRequest(config => {
    // Set default value to hideDefaultToast for each request
    hideDefaultToast = false
    const requestKey = getRequestKey(config)

    // If the route is error-code route, and language-strings route then ignore the cancelToken
    // Because these routes are public routes and we need to fetch these routes without cancelToken
    const isPublicRouteToFetch =
      config.url.includes('master/language-strings') ||
      config.url.includes('white-label/error-strings')

    // Create temporary cancel token if not already exists
    if (!config.cancelToken && !isPublicRouteToFetch) {
      // Create a new cancel token
      const source = context.$axios.CancelToken.source()
      config.cancelToken = source.token
      pendingRequests.set(requestKey, source)
    }

    // Getting paths for dynamic routes
    const result = accessObject(config.url)
    if (result?.user) config.url = result?.user

    const params = { ...config.params } || {}
    // This param is for DateButtonFilters Component purpose only
    if (params.selectedDate) params.selectedDate = null

    // Check if the request have dates
    if (params.start_date) params.start_date = utcTime(params.start_date)
    if (params.end_date) params.end_date = utcTime(params.end_date)
    if (params?.start_month) params.start_date = params.start_month
    if (params?.end_month) params.end_date = params.end_month
    // What is keepCurrency for?
    // Don't use the global currency
    if (params.currency && !params.keepCurrency)
      params.currency = context.store.state.settings.globalSelectedCurrency
    if (params?.notFire) delete params.notFire

    // To remove null values from parameter
    for (const [key, value] of Object.entries(params)) {
      if (value || value === false) {
        config.params[key] = value
      } else {
        delete config.params[key]
      }
    }
    if (config?.params?.start_month) delete config.params.start_month
    if (config?.params?.end_month) delete config.params.end_month
    if (params?.keepCurrency) delete config.params.keepCurrency
    if (params?.notFire || params?.notFire === false) delete config.params.notFire
    if (params?.hideToast) {
      hideDefaultToast = true
      delete config.params.hideToast
    }

    config.baseURL = context.app.$config.API
    config.headers.common.Authorization = `Bearer ${context.$cookie?.get('token')}`
    if (context.$cookie?.get('currency')) {
      config.headers.common.currency = `${context.$cookie?.get('currency')}`
    }
  })

  // Check if response is in JSON.
  context.$axios.interceptors.response.use(
    response => {
      const requestKey = getRequestKey(response.config)
      pendingRequests.delete(requestKey)

      // The success toast will work only when the response is in error message format
      if (
        typeof response?.data?.code === 'number' &&
        response?.data?.error === false &&
        hideDefaultToast === false
      ) {
        const { message: responseMessage, code: responseMessageStatusCode } = response.data

        const errorStrings = context.store.state['website-setting']['error-code'].errorStrings
        const displayMessageText = errorStrings[responseMessageStatusCode] || responseMessage
        context.$toast.success(`${displayMessageText}`)
      }
      const isExist = response.headers['content-type']?.indexOf('application/json')
      return isExist !== -1 ? response : Promise.reject(response)
    },
    error => {
      return retryRequest(error)
    }
  )

  // Error handling
  context.$axios.onError(error => {
    const {
      error: errorStatus,
      code: errorCode,
      message: errorMessage
    } = error?.response?.data || {}

    // API currency header Bad Request
    // if (error?.response?.data?.code === 95001) {
    //     handleClientSideLogout(context)
    //     return false
    // }

    const errorStrings = context.store.state['website-setting']['error-code'].errorStrings
    const errorMessageByErrorString = errorStrings[errorCode]
    const displayErrorText = errorMessageByErrorString || errorMessage

    // Once user Unauthorized then moved them to login page.
    // 95009 is the forbidden IP error code
    if (
      error.response?.status === 401 ||
      (error.response?.status === 403 && error?.response?.data?.code === 95009)
    ) {
      cancelAllRequests()
      const isHttpRequestFailByCredentialOrAuth =
        context.store.state.settings.isHttpRequestFailByCredentialOrAuth
      handleClientSideLogout(context)

      if (isHttpRequestFailByCredentialOrAuth === false) {
        context.$toast.clear()
        context.$toast.error(displayErrorText)

        if (!error?.request?.responseURL?.includes('/login')) {
          context.store.dispatch('settings/setHttpRequestFailStatus', true)
        }
      }
      return false
    }

    // Clear old toast before showing the new one
    if (error?.response) {
      // Do not clear the existing toast, if the request is CancelByToken
      context.$toast.clear()
    }

    if (error?.response && error?.response?.data) {
      try {
        // Only if the error-message is error;
        if (errorStatus) {
          context.$toast.error(displayErrorText)
        }
      } catch (error) {}
    } else if (error.message !== '') {
      const errorStrings = context.store.state['website-setting']['error-code'].errorStrings
      if (!navigator.onLine || error?.message === 'Network Error') {
        // No Internet Connection, error-code: 95012
        context.$toast.clear()
        context.$toast.error(errorStrings[95012] || 'Network Errors')
      } else if (error.code === 'ECONNABORTED') {
        // Timeout error case, error-code: 95014
        context.$toast.error(errorStrings[95014] || 'API response timeouts')
      } else if (error.response?.status === 429) {
        // Too many API requests, error-code: 95013
        context.$toast.error(errorStrings[95013] || 'API Rate Limiting')
      } else {
        context.$toast.error('Try again after some time!')
      }
    }
    if (error.response?.status === 403) {
      const userData = context.$cookie.get('user') || {}
      userData.user_type = 2
      context.$cookie.set('user', userData)
      context.store.dispatch('settings/redirectToHasPermissionRoute')
    }
    return false
  })

  const retryRequest = async error => {
    const { config, message } = error
    if (!config || !config.retry) return Promise.reject(error)

    if (message.includes('timeout') || message.includes('Network Error')) {
      config.retry -= 1
      await new Promise(resolve => setTimeout(resolve, config.retryDelay || 1000))
      return context.$axios(config)
    }
    return Promise.reject(error)
  }
}

const cancelAllRequests = () => {
  pendingRequests.forEach(source => source.cancel(''))
  pendingRequests.clear()
}

const handleClientSideLogout = context => {
  context.$cookie.remove('token')
  context.$cookie.remove('user')
  context.app.router.push('/login')
}
